import { FunctionalComponent } from 'preact';
import { useCallback, useEffect, useState, useRef } from 'preact/hooks';
import { logger } from '@accedo/logger';
import { Nav } from '@accedo/spatial-nav';
import { useDialog } from '@accedo/ctv-ui-dialog';
import { SplashScreen } from '##/components/SplashScreen';
import { ServiceManager } from '##/services/elevate/manager';
import { ElevateConfig } from '##/dataModels/config';

import { AppRouter } from '##/services/routing/AppRouter';
import { MenuItem } from '##/dataModels/menuItem';
import MenuWrapper from '##/components/MenuWrapper/MenuWrapper';
import { loadDefaultPageLayout, loadMenuItems, loadRoutes } from '##/utils/appContent';
import { config } from '##/services/store';
import { ErrorDialog } from '##/components/Dialogs/ErrorDialog';
import { checkNetworkConnection } from '##/utils/deviceUtils';
import { NetworkErrorDialog } from '##/components/Dialogs/NetworkErrorDialog';
import { useSubscriptionContext } from '##/context/SubscriptionContext';
import { useAuthContext } from '##/context/AuthContext';
import { setLoadingIndicatorVisibility } from '##/components/LoadingIndicator';

const NETWORK_CHECK_INTERVAL = 30 * 1000;

export const AppContent: FunctionalComponent = () => {
  const [loaded, setLoaded] = useState<boolean>(false);
  const [routesLoaded, setRoutesLoaded] = useState<boolean>(false);
  const [isNetworkConnected, setIsNetworkConnected] = useState<boolean>(true);
  const { open: openDialog } = useDialog();
  const { user } = useAuthContext();
  const { fetchSubscriptions, fetchEntitlements } = useSubscriptionContext();

  const { token } = user?.authToken || {};

  const networkInterval = useRef<NodeJS.Timeout>();

  const checkAndHandleNetwork = useCallback(async (): Promise<void> => {
    try {
      const isConnected = await checkNetworkConnection();

      if (!isConnected) {
        if (isNetworkConnected) {
          setIsNetworkConnected(false);
        }
        openDialog(<NetworkErrorDialog onRefresh={checkAndHandleNetwork} />);
        return;
      }

      logger.debug('Network is connected');

      if (!isNetworkConnected) {
        setIsNetworkConnected(true);
      }
    } catch (error) {
      // fetch throws when device is offline — treat same as no connection
      logger.error('Error checking network connection:', error);
      if (isNetworkConnected) {
        setIsNetworkConnected(false);
      }
      openDialog(<NetworkErrorDialog onRefresh={checkAndHandleNetwork} />);
    }
  }, [isNetworkConnected, openDialog]);

  const startInternetCheck = useCallback((): void => {
    clearInterval(networkInterval.current);

    networkInterval.current = setInterval(checkAndHandleNetwork, NETWORK_CHECK_INTERVAL);
  }, [checkAndHandleNetwork]);

  useEffect(() => {
    startInternetCheck();

    return () => clearInterval(networkInterval.current);
  }, [startInternetCheck]);

  const loadInitialData = useCallback(async () => {
    // Elevate Config from Accedo One Control / ConfigService
    // Config is public and doesn't require authentication
    let elevateConfig: ElevateConfig = config.getByKey<ElevateConfig>('elevateConfig');
    let defaultRoute: string = '/';

    if (!elevateConfig) {
      try {
        elevateConfig = await ServiceManager.getConfigService().getConfig();
        // NZOS API doesn't provide defaultRoute, using hardcoded default
        defaultRoute = elevateConfig.application?.navigation?.defaultRoute || '/';
        config.set({ defaultRoute });
        config.set({ isFirstRoute: true });
        logger.debug('defaultRoute is ', defaultRoute);
      } catch (error) {
        logger.error('Failed to retrieve elevateConfig:', error);
        openDialog(<ErrorDialog />);
        return;
      }
    }

    // Always store config in memory (whether from cache or fresh API call)
    // This ensures the in-memory store stays in sync with sessionStorage
    config.set({ elevateConfig });

    // Config loaded successfully
    if (elevateConfig) {
      logger.debug('Initial public data loaded');
      setLoaded(true);
    }
  }, []);

  const loadAuthenticatedData = useCallback(async () => {
    // Load menu items, routes, categories, and default layout after authentication
    const defaultRoute = config.getByKey<string>('defaultRoute') || '/';

    logger.info('[AppContent] Loading authenticated data (menu, routes, categories, layouts)');

    // Load menu items first, then routes will use cached menu
    await loadMenuItems();

    // Load routes and default layout in parallel (routes will use cached menu)
    await Promise.all([loadRoutes(), loadDefaultPageLayout(defaultRoute)]);

    logger.info('[AppContent] Authenticated data loaded successfully');

    // Trigger re-render so AppRouter picks up the newly loaded routes
    setRoutesLoaded(true);
  }, []);

  useEffect(() => {
    if (!token) {
      return;
    }

    // When user logs in, fetch subscriptions, entitlements, AND authenticated data
    fetchSubscriptions(token);
    fetchEntitlements(token);
    loadAuthenticatedData();
  }, [token, fetchSubscriptions, fetchEntitlements, loadAuthenticatedData]);

  // Load initial config on mount only - deliberately using empty deps to run once

  useEffect(() => {
    logger.debug('AppContent useEffect');
    loadInitialData();
  }, []);

  useEffect(() => {
    // For authenticated users waiting for routes to load, show loading indicator
    if (token && !routesLoaded) {
      setLoadingIndicatorVisibility(true);
    }
  }, [token, routesLoaded]);

  if (!loaded) {
    return <SplashScreen />;
  }

  // For authenticated users, wait for routes to load before showing router
  // This prevents 404 flash when reloading authenticated pages
  if (token && !routesLoaded) {
    return null; // Loading indicator is shown via useEffect above
  }

  return (
    <Nav.Vertical id="content">
      <MenuWrapper
        id="menu-container"
        key="menu-container"
        items={config.getByKey<MenuItem[]>('menuItems') || []}
        index={0}
      />
      {/* Key changes when routesLoaded changes, forcing AppRouter to re-render with new routes */}
      <AppRouter key={routesLoaded ? 'router-loaded' : 'router-initial'} />
    </Nav.Vertical>
  );
};
